import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import DenialModal from '../components/DenialModal';
import { formatBytes, getUploadFileForSubmit, prepareUploadFile } from '../utils/uploadFiles';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function locationLabel(loc) {
  return loc === 'church' ? 'Church' : "Pastor's House";
}

function priorityOf(ticket) {
  return ticket.trustee_priority || ticket.submitter_priority;
}

function isImage(mimeType) {
  return mimeType && mimeType.startsWith('image/');
}

function PreparedFileList({ files, onRemove, onToggleOriginal }) {
  if (files.length === 0) return null;

  return (
    <div className="file-list">
      {files.map((file) => {
        const activeFile = getUploadFileForSubmit(file);
        const wasCompressed = !!file.compressedFile && !file.useOriginal;

        return (
          <div key={file.id} className="file-chip">
            <span className="file-chip-main">
              <span className="file-chip-name">{activeFile.name}</span>
              <span className="file-chip-size">
                {wasCompressed
                  ? `Compressed from ${formatBytes(file.originalFile.size)} to ${formatBytes(activeFile.size)}`
                  : file.compressedFile && file.useOriginal
                  ? `Using original ${formatBytes(file.originalFile.size)} · compressed version is ${formatBytes(file.compressedFile.size)}`
                  : `${formatBytes(activeFile.size)}${file.compressionError ? ' · using original' : ''}`}
              </span>
            </span>
            {file.compressedFile && (
              <button type="button" className="file-chip-action" onClick={() => onToggleOriginal(file.id)}>
                {file.useOriginal ? 'Compress' : 'Original'}
              </button>
            )}
            <button type="button" className="file-chip-remove" onClick={() => onRemove(file.id)} aria-label="Remove file">&times;</button>
          </div>
        );
      })}
    </div>
  );
}

// Inline editable text field
function EditableText({ value, onSave, multiline, className, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef();

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== value) onSave(draft.trim());
    else setDraft(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); save(); }
    if (e.key === 'Enter' && e.metaKey && multiline) { e.preventDefault(); save(); }
  };

  if (!editing) {
    return (
      <div className={`editable ${className || ''}`} onClick={() => setEditing(true)} title="Click to edit">
        {value || <span className="editable-placeholder">{placeholder || 'Click to add…'}</span>}
      </div>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={ref}
        className={`editable-input editable-textarea ${className || ''}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        rows={4}
      />
    );
  }

  return (
    <input
      ref={ref}
      type="text"
      className={`editable-input ${className || ''}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={handleKeyDown}
    />
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [trustees, setTrustees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const isAdmin = !!user;
  const canCleanSpam = !!user?.isAdmin;

  // Update form
  const [showOriginal, setShowOriginal] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editFiles, setEditFiles] = useState([]);
  const editFileRef = useRef();
  const [openMenu, setOpenMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [note, setNote] = useState('');
  const [postPrivate, setPostPrivate] = useState(false);
  const [updateFiles, setUpdateFiles] = useState([]);
  const [processingUpdateFiles, setProcessingUpdateFiles] = useState(false);
  const [processingEditFiles, setProcessingEditFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [moderating, setModerating] = useState(null);
  const updateFileRef = useRef();

  const prepareSelectedFiles = async (event, setFileState, setProcessingState) => {
    const selectedFiles = Array.from(event.target.files);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    setProcessingState(true);
    try {
      const preparedFiles = await Promise.all(selectedFiles.map(prepareUploadFile));
      setFileState(prev => [...prev, ...preparedFiles]);
    } finally {
      setProcessingState(false);
    }
  };

  const removePreparedFile = (setFileState, fileId) => {
    setFileState(prev => prev.filter(file => file.id !== fileId));
  };

  const togglePreparedFileOriginal = (setFileState, fileId) => {
    setFileState(prev => prev.map(file =>
      file.id === fileId ? { ...file, useOriginal: !file.useOriginal } : file
    ));
  };

  // Close update menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handler = () => setOpenMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenu]);

  const refreshAuditLog = useCallback(async () => {
    if (auditLog) {
      try {
        const data = await api.getAuditLog(id);
        setAuditLog(data);
      } catch { /* ignore */ }
    }
  }, [id, auditLog]);

  const loadTicket = useCallback(async () => {
    try {
      const data = await api.getTicket(id);
      setTicket(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
    if (isAdmin) {
      api.getTrustees().then(setTrustees).catch(() => {});
    }
  }, [loadTicket, isAdmin]);

  const handleFieldChange = async (field, value) => {
    if (field === 'status' && value === 'denied') {
      setShowDenialModal(true);
      return;
    }
    try {
      await api.updateTicket(id, { [field]: value });
      loadTicket();
      refreshAuditLog();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeny = async (reason) => {
    try {
      await api.updateTicket(id, { status: 'denied', denial_reason: reason });
      loadTicket();
    } catch (err) {
      setError(err.message);
    } finally {
      setShowDenialModal(false);
    }
  };

  const handleModerate = async (attachmentId, approved) => {
    setModerating(attachmentId);
    try {
      await api.moderateAttachment(id, attachmentId, approved);
      loadTicket();
    } catch (err) {
      setError(err.message);
    } finally {
      setModerating(null);
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('note', note.trim());
      if (postPrivate) formData.append('hidden', 'true');
      updateFiles.forEach(f => formData.append('files', getUploadFileForSubmit(f)));
      await api.addUpdate(id, formData);
      setNote('');
      setPostPrivate(false);
      setUpdateFiles([]);
      loadTicket();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditUpdate = async (updateId) => {
    if (!editDraft.trim()) return;
    try {
      const formData = new FormData();
      formData.append('note', editDraft.trim());
      editFiles.forEach(f => formData.append('files', getUploadFileForSubmit(f)));
      await api.editUpdate(id, updateId, formData);
      setEditingUpdate(null);
      setEditDraft('');
      setEditFiles([]);
      loadTicket();
      refreshAuditLog();
    } catch (err) { setError(err.message); }
  };

  const handleHideUpdate = async (updateId, hidden) => {
    try {
      await api.editUpdate(id, updateId, { hidden });
      loadTicket();
      refreshAuditLog();
    } catch (err) { setError(err.message); }
  };

  const handleDeleteUpdate = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteUpdate(id, deleteTarget);
      setDeleteTarget(null);
      loadTicket();
      refreshAuditLog();
    } catch (err) { setError(err.message); }
  };

  const handleSpamToggle = async () => {
    const newStatus = ticket.status === 'spam' ? 'submitted' : 'spam';
    try {
      await api.updateTicket(id, { status: newStatus });
      loadTicket();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="spinner" />;
  if (!ticket) return <div className="page container"><div className="alert alert-error">{error || 'Ticket not found.'}</div></div>;

  const priority = priorityOf(ticket);

  return (
    <div className="page container">
      <div className="ticket-detail">
        <div className="ticket-top-bar">
          <Link to="/board" className="back-link">&larr; Back to Board</Link>
          {isAdmin && ticket.status !== 'spam' && (
            <button className="spam-flag-btn" onClick={handleSpamToggle}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              This is spam
            </button>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Spam banner */}
        {isAdmin && ticket.status === 'spam' && (
          <div className="spam-banner">
            <div className="spam-banner-main">
              <span>This request is marked as spam and hidden from the public.</span>
              <button className="btn btn-primary btn-sm" onClick={handleSpamToggle}>This is not spam</button>
            </div>
          </div>
        )}

        {/* Submitter info — admin only, editable */}
        {isAdmin && ticket.submitter_name && (
          <div className="submitter-info">
            <div className="submitter-info-title">Submitter (visible only to trustees)</div>
            <div className="submitter-fields">
              <div className="submitter-field">
                <span className="submitter-field-label">Name</span>
                <EditableText value={ticket.submitter_name} onSave={(v) => handleFieldChange('submitter_name', v)} className="submitter-field-value" />
              </div>
              <div className="submitter-field">
                <span className="submitter-field-label">Email</span>
                <EditableText value={ticket.submitter_email || ''} onSave={(v) => handleFieldChange('submitter_email', v)} className="submitter-field-value" placeholder="No email" />
              </div>
              <div className="submitter-field">
                <span className="submitter-field-label">Phone</span>
                <EditableText value={ticket.submitter_phone || ''} onSave={(v) => handleFieldChange('submitter_phone', v)} className="submitter-field-value" placeholder="No phone" />
              </div>
            </div>
          </div>
        )}

        {/* Header — editable title for admins */}
        <div className="ticket-header">
          <div className="ticket-number">Request #{ticket.ticket_number}</div>
          {isAdmin ? (
            <EditableText
              value={ticket.title}
              onSave={(v) => handleFieldChange('title', v)}
              className="ticket-title"
            />
          ) : (
            <h1 className="ticket-title">{ticket.title}</h1>
          )}
          <div className="ticket-meta">
            <span className={`badge badge-${ticket.status}`}>{ticket.status.replace('_', ' ')}</span>
            <span className={`badge badge-${priority}`}>{priority}</span>
            <span className="ticket-meta-item">{locationLabel(ticket.location)}</span>
            {ticket.specific_location && (
              <span className="ticket-meta-item ticket-meta-muted">{ticket.specific_location}</span>
            )}
            <span className="ticket-meta-item">{formatDate(ticket.created_at)}</span>
            {ticket.assigned_trustee_name && (
              <span className="ticket-meta-item ticket-meta-gold">Assigned to {ticket.assigned_trustee_name}</span>
            )}
          </div>
        </div>

        {/* Description — editable for admins */}
        <div className="ticket-body glass">
          {isAdmin ? (
            <EditableText
              value={ticket.description}
              onSave={(v) => handleFieldChange('description', v)}
              multiline
              className="ticket-description"
            />
          ) : (
            <div className="ticket-description">{ticket.description}</div>
          )}

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div className="attachments-section">
              <div className="attachments-header">
                <span className="attachments-count">{ticket.attachments.length} file{ticket.attachments.length !== 1 ? 's' : ''}</span>
                {isAdmin && (
                  <button className="attachment-download-all" onClick={() => {
                    window.open(`/api/tickets/${id}/attachments/download`, '_blank');
                  }}>
                    Download all
                  </button>
                )}
              </div>
              <div className="ticket-attachments">
                {ticket.attachments.map((att) => (
                  <div key={att.id} className="attachment-card">
                    <a className="attachment-link" href={`/uploads/${att.stored_name}`} target="_blank" rel="noopener noreferrer">
                      <div className="attachment-thumb">
                        {isImage(att.mime_type) ? (
                          <img src={`/uploads/${att.stored_name}`} alt={att.original_name} />
                        ) : (
                          <div className="attachment-file-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            {att.mime_type === 'application/pdf' && <span>PDF</span>}
                          </div>
                        )}
                      </div>
                      <span className="attachment-name">{att.original_name}</span>
                    </a>
                    {isAdmin && (
                      <div className="attachment-actions">
                        <label className="visibility-switch visibility-switch-sm" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={!att.approved} onChange={(e) => { e.preventDefault(); handleModerate(att.id, !att.approved); }} disabled={moderating === att.id} />
                          <span className="visibility-switch-label">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Trustees only
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Original submission — collapsible, admin only, shows when anything was edited */}
        {isAdmin && ticket.original_title && (
          ticket.original_title !== ticket.title ||
          ticket.original_description !== ticket.description ||
          ticket.original_submitter_name !== ticket.submitter_name ||
          (ticket.original_submitter_email || '') !== (ticket.submitter_email || '') ||
          (ticket.original_submitter_phone || '') !== (ticket.submitter_phone || '')
        ) && (
          <div className="original-submission">
            <button className="original-submission-toggle" onClick={() => setShowOriginal(!showOriginal)}>
              <span className={`ticket-group-chevron ${!showOriginal ? 'collapsed' : ''}`}>›</span>
              View original submission
            </button>
            {showOriginal && (
              <div className="original-submission-body glass">
                {ticket.original_title !== ticket.title && (
                  <>
                    <p className="original-submission-label">Original Title</p>
                    <p className="original-submission-text">{ticket.original_title}</p>
                  </>
                )}
                {ticket.original_description !== ticket.description && (
                  <>
                    <p className="original-submission-label">Original Description</p>
                    <p className="original-submission-text">{ticket.original_description}</p>
                  </>
                )}
                {(ticket.original_submitter_name !== ticket.submitter_name ||
                  (ticket.original_submitter_email || '') !== (ticket.submitter_email || '') ||
                  (ticket.original_submitter_phone || '') !== (ticket.submitter_phone || '')) && (
                  <>
                    <p className="original-submission-label">Original Contact Info</p>
                    <p className="original-submission-text">
                      {ticket.original_submitter_name}
                      {ticket.original_submitter_email && <> &middot; {ticket.original_submitter_email}</>}
                      {ticket.original_submitter_phone && <> &middot; {ticket.original_submitter_phone}</>}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Denial reason — editable */}
        {ticket.status === 'denied' && ticket.denial_reason && (
          <div className="alert alert-error">
            <div className="denial-heading">Denied</div>
            {isAdmin ? (
              <EditableText
                value={ticket.denial_reason}
                onSave={(v) => handleFieldChange('denial_reason', v)}
              />
            ) : (
              <div>{ticket.denial_reason}</div>
            )}
          </div>
        )}


        {/* Admin: Workflow actions + Details */}
        {isAdmin && (() => {
          const NEXT_ACTIONS = {
            submitted: [
              { status: 'reviewed', label: 'Mark Reviewed', style: 'btn-primary' },
            ],
            reviewed: [
              { status: 'approved', label: 'Approve', style: 'btn-primary' },
              { status: 'denied', label: 'Deny', style: 'btn-danger' },
            ],
            approved: [
              { status: 'in_progress', label: 'Start Work', style: 'btn-primary' },
            ],
            in_progress: [
              { status: 'completed', label: 'Mark Complete', style: 'btn-primary' },
            ],
            completed: [],
            denied: [
              { status: 'submitted', label: 'Reopen', style: 'btn-secondary' },
            ],
            spam: [],
          };
          const actions = NEXT_ACTIONS[ticket.status] || [];

          return (
            <>
              {/* Workflow bar */}
              <div className="workflow-bar glass">
                <div className="workflow-status">
                  <span className="form-label">Current Status</span>
                  <span className={`badge badge-${ticket.status}`}>{ticket.status.replace('_', ' ')}</span>
                </div>
                {actions.length > 0 && (
                  <div className="workflow-actions">
                    {actions.map(a => (
                      <button
                        key={a.status}
                        className={`btn ${a.style}`}
                        onClick={() => handleFieldChange('status', a.status)}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="workflow-override">
                  <select className="form-select form-select-sm" value={ticket.status} onChange={(e) => handleFieldChange('status', e.target.value)}>
                    <option value="submitted">Submitted</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="approved">Approved</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>

              {/* Details panel */}
              <div className="details-panel glass">
                <div className="details-header">
                  <h2 className="section-label">Details</h2>
                  <div className="details-cost-pills">
                    {ticket.estimated_cost && <span className="badge badge-cost">Est. ${Number(ticket.estimated_cost).toLocaleString()}</span>}
                    {ticket.final_cost && <span className="badge badge-cost-final">Final ${Number(ticket.final_cost).toLocaleString()}</span>}
                  </div>
                </div>
                <div className="details-grid">
                  <div className="details-row">
                    <span className="details-label">Priority</span>
                    <select className="form-select form-select-sm" value={ticket.trustee_priority || ''} onChange={(e) => handleFieldChange('trustee_priority', e.target.value)}>
                      <option value="">Submitter's ({ticket.submitter_priority})</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="details-row">
                    <span className="details-label">Assigned To</span>
                    <select className="form-select form-select-sm" value={ticket.assigned_trustee_id || ''} onChange={(e) => handleFieldChange('assigned_trustee_id', e.target.value)}>
                      <option value="">Unassigned</option>
                      {trustees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="details-row">
                    <span className="details-label">Location</span>
                    <select className="form-select form-select-sm" value={ticket.location} onChange={(e) => handleFieldChange('location', e.target.value)}>
                      <option value="church">Church</option>
                      <option value="pastors_house">Pastor's House</option>
                    </select>
                  </div>
                  <div className="details-row">
                    <span className="details-label">Est. Cost</span>
                    <input type="number" className="form-input form-input-sm" placeholder="—" defaultValue={ticket.estimated_cost || ''} onBlur={(e) => handleFieldChange('estimated_cost', e.target.value)} key={`est-${ticket.estimated_cost}`} />
                  </div>
                  <div className="details-row">
                    <span className="details-label">Final Cost</span>
                    <input type="number" className="form-input form-input-sm" placeholder="—" defaultValue={ticket.final_cost || ''} onBlur={(e) => handleFieldChange('final_cost', e.target.value)} key={`final-${ticket.final_cost}`} />
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* Updates — public-facing, intentional posts */}
        {(() => {
          const publicUpdates = ticket.updates || [];

          return (
            <>
              <div className="updates-section glass">
                <h2 className="section-label">Updates</h2>

                {isAdmin && (
                  <form onSubmit={handleAddUpdate} className="update-composer">
                    <textarea className="update-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write an update…" required />
                    <div className="update-composer-bar">
                      <div className="update-composer-left">
                        <button type="button" className="update-attach-btn" onClick={() => updateFileRef.current?.click()}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                          Attach
                        </button>
                        <input ref={updateFileRef} type="file" multiple accept="image/*,.pdf" className="visually-hidden" onChange={(e) => prepareSelectedFiles(e, setUpdateFiles, setProcessingUpdateFiles)} />
                        <label className="visibility-switch">
                          <input type="checkbox" checked={postPrivate} onChange={() => setPostPrivate(!postPrivate)} />
                          <span className="visibility-switch-label">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Trustees only
                          </span>
                        </label>
                      </div>
                      <button type="submit" className="btn btn-primary btn-sm" disabled={saving || processingUpdateFiles || !note.trim()}>
                        {saving ? 'Posting…' : processingUpdateFiles ? 'Preparing…' : 'Post Update'}
                      </button>
                    </div>
                    <PreparedFileList
                      files={updateFiles}
                      onRemove={(fileId) => removePreparedFile(setUpdateFiles, fileId)}
                      onToggleOriginal={(fileId) => togglePreparedFileOriginal(setUpdateFiles, fileId)}
                    />
                  </form>
                )}

                <div className="updates-list">
                  {publicUpdates.length > 0 ? (
                    publicUpdates.map((update) => (
                      <div key={update.id} className={`update-entry ${update.hidden ? 'update-entry-private' : ''}`}>
                        <div className="update-entry-header">
                          <span className="update-entry-author">{update.trustee_name}</span>
                          <span className="update-entry-time">{formatDateTime(update.created_at)}</span>
                          {isAdmin && update.hidden && (
                            <span className="update-private-badge">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              Private
                            </span>
                          )}
                          {isAdmin && (
                            <div className="update-menu-wrap">
                              <button className="update-menu-trigger" onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === update.id ? null : update.id); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                              </button>
                              {openMenu === update.id && (
                                <div className="update-menu">
                                  <button onClick={() => { setEditingUpdate(update.id); setEditDraft(update.note); setEditFiles([]); setOpenMenu(null); }}>Edit</button>
                                  <button onClick={() => { handleHideUpdate(update.id, !update.hidden); setOpenMenu(null); }}>{update.hidden ? 'Make public' : 'Make private'}</button>
                                  <button className="update-action-danger" onClick={() => { setDeleteTarget(update.id); setOpenMenu(null); }}>Delete</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {editingUpdate === update.id ? (
                          <div className="update-edit-form">
                            <textarea className="update-input update-input-edit" value={editDraft} onChange={(e) => setEditDraft(e.target.value)} rows={3} />
                            <div className="update-edit-actions">
                              <button type="button" className="update-attach-btn" onClick={() => editFileRef.current?.click()}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                Attach
                              </button>
                              <input ref={editFileRef} type="file" multiple accept="image/*,.pdf" className="visually-hidden" onChange={(e) => prepareSelectedFiles(e, setEditFiles, setProcessingEditFiles)} />
                              <button className="btn btn-secondary btn-sm" onClick={() => { setEditingUpdate(null); setEditFiles([]); }}>Cancel</button>
                              <button className="btn btn-primary btn-sm" disabled={processingEditFiles} onClick={() => handleEditUpdate(update.id)}>{processingEditFiles ? 'Preparing…' : 'Save'}</button>
                            </div>
                            <PreparedFileList
                              files={editFiles}
                              onRemove={(fileId) => removePreparedFile(setEditFiles, fileId)}
                              onToggleOriginal={(fileId) => togglePreparedFileOriginal(setEditFiles, fileId)}
                            />
                          </div>
                        ) : (
                          <div className="update-entry-body">{update.note}</div>
                        )}
                        {update.attachments && update.attachments.length > 0 && (
                          <div className="ticket-attachments update-attachments">
                            {update.attachments.map((att) => (
                              <div key={att.id} className="attachment-card">
                                <a className="attachment-link" href={`/uploads/${att.stored_name}`} target="_blank" rel="noopener noreferrer">
                                  <div className="attachment-thumb">
                                    {isImage(att.mime_type) ? (
                                      <img src={`/uploads/${att.stored_name}`} alt={att.original_name} />
                                    ) : (
                                      <div className="attachment-file-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        {att.mime_type === 'application/pdf' && <span>PDF</span>}
                                      </div>
                                    )}
                                  </div>
                                  <span className="attachment-name">{att.original_name}</span>
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    !isAdmin && <p className="empty-state-text">No updates yet.</p>
                  )}
                </div>
              </div>

              {/* Activity log — admin only, lazy loaded */}
              {isAdmin && (
                <div className="activity-log-section">
                  <button className="activity-log-toggle" onClick={async () => {
                    if (auditLog) { setAuditLog(null); return; }
                    setAuditLoading(true);
                    try {
                      const data = await api.getAuditLog(id);
                      setAuditLog(data);
                    } catch (err) { setError(err.message); }
                    finally { setAuditLoading(false); }
                  }}>
                    {auditLoading ? 'Loading…' : auditLog ? 'Hide activity log' : 'Show activity log'}
                  </button>
                  {auditLog && auditLog.length > 0 && (
                    <div className="activity-log">
                      {auditLog.map((entry) => (
                        <div key={entry.id} className="activity-row">
                          <span className="activity-row-author">{entry.trustee_name}</span>
                          <span className="activity-row-content">{entry.note}</span>
                          <span className="activity-row-time">{formatDateTime(entry.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {auditLog && auditLog.length === 0 && (
                    <p className="empty-state-text">No activity recorded yet.</p>
                  )}
                </div>
              )}
            </>
          );
        })()}

      </div>

      {showDenialModal && (
        <DenialModal onConfirm={handleDeny} onCancel={() => setShowDenialModal(false)} />
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Update</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
              This will permanently remove this update. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteUpdate}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
